import { find, mapMaybe } from '@execonline-inc/collections';
import { toTask } from '@execonline-inc/maybe-adapter';
import { URLParser } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Maybe, fromNullable, just, nothing } from 'maybeasy';
import { SingleValue } from 'react-select';
import { Result, err, ok } from 'resulty';
import { Task } from 'taskarian';
import { formatBytesDecimal } from '../../../FormatBytes';
import { fromResult } from '../../../TaskExt';
import { Link, Rel, Resource } from '../../Types';
import { validateFields } from '../Validators';

export type FieldMode = 'readonly' | 'required' | 'writable';

export interface FormFieldBase<K extends string> {
  kind: K;
  name: string;
  label: Maybe<string>;
  errors: ReadonlyArray<string>;
  mode: FieldMode;
}

export const isRequired = (field: FormField): boolean => {
  switch (field.mode) {
    case 'required':
      return true;
    case 'readonly':
    case 'writable':
      return false;
  }
};

export const isReadonly = (field: FormField): boolean => {
  switch (field.mode) {
    case 'readonly':
      return true;
    case 'required':
    case 'writable':
      return false;
  }
};

export type TextContentType = `text/${string}`;

export interface Content {
  type: TextContentType;
  multiline: boolean;
}

const baseTextContentType: TextContentType = 'text/';

export const isTextContentType = (content: string): content is TextContentType =>
  content.startsWith(baseTextContentType);

export interface StringField extends FormFieldBase<'string'> {
  value: string;
  content: Content;
  minlength: Maybe<number>;
  maxlength: number;
  pattern: Maybe<RegExp>;
  placeholder: Maybe<string>;
}

export interface BooleanField extends FormFieldBase<'boolean'> {
  value: boolean;
}

export type DateScope = 'year' | 'month' | 'day' | 'minute' | 'second' | 'millisecond';

export interface DateField extends FormFieldBase<'date'> {
  value: Maybe<Date>;
  min: Maybe<Date>;
  max: Maybe<Date>;
  scope: DateScope;
}

export interface NumericField extends FormFieldBase<'number'> {
  value: Maybe<number>;
  min: Maybe<number>;
  max: Maybe<number>;
}

export type ImageContentType = `image/${string}`;

const baseImageContentType: ImageContentType = 'image/';

export const isImageContentType = (content: string): content is ImageContentType =>
  content.startsWith(baseImageContentType);

export interface OptionImage {
  type: ImageContentType;
  data: string;
  src: URLParser;
  alt: string;
}

export interface SelectionOption {
  label: string;
  value: string;
  image: Maybe<OptionImage>;
  link: Maybe<Link>;
}

export type SelectionOptionResource = Resource<Array<SelectionOption>>;

export interface SelectionOptionWithResource<T> extends SelectionOption {
  resource: Resource<T>;
}

export type SelectionOptionResourceWithInnerResource<T> = Resource<
  Array<SelectionOptionWithResource<T>>
>;

export interface OptionsListAndRel {
  kind: 'options-list-and-rel';
  options: ReadonlyArray<SelectionOption>;
  optionsRel: Rel;
}

export interface OptionsList {
  kind: 'options-list';
  options: ReadonlyArray<SelectionOption>;
}

export interface OptionsRel {
  kind: 'options-rel';
  optionsRel: Rel;
}

export type OptionsListOrRelOrBoth = OptionsListAndRel | OptionsList | OptionsRel;

export type SelectionsField = SingleSelectionField | MultipleSelectionsField;

export interface SingleSelectionField extends FormFieldBase<'single-selection'> {
  options: OptionsListOrRelOrBoth;
  links: ReadonlyArray<Link>;
  minSelections: number;
  maxSelections: number;
  value: SingleValue<SelectionOption>;
}

export interface MultipleSelectionsField extends FormFieldBase<'multiple-selection'> {
  options: OptionsListOrRelOrBoth;
  links: ReadonlyArray<Link>;
  minSelections: number;
  maxSelections: number;
  value: ReadonlyArray<SelectionOption>;
}

export const isSelected = (field: MultipleSelectionsField, value: string) =>
  field.value.map((val) => val.value === value);

export interface SyncMultipleSelectionField extends MultipleSelectionsField {
  options: OptionsListAndRel | OptionsList;
}

export interface AsyncMultipleSelectionField extends MultipleSelectionsField {
  options: OptionsListAndRel | OptionsRel;
}

export const whenSyncOptions = (
  field: MultipleSelectionsField,
): Maybe<SyncMultipleSelectionField> => {
  switch (field.options.kind) {
    case 'options-list-and-rel':
    case 'options-list':
      return just({ ...field, options: field.options });
    case 'options-rel':
      return nothing();
  }
};

export const whenAsyncOptions = (
  field: MultipleSelectionsField,
): Maybe<AsyncMultipleSelectionField> => {
  switch (field.options.kind) {
    case 'options-list-and-rel':
    case 'options-rel':
      return just({ ...field, options: field.options });
    case 'options-list':
      return nothing();
  }
};

export interface UploadLocationRequest {
  fieldName: string;
  filename: string;
}

export const uploadLocationRequest = (field: FileField, file: File): UploadLocationRequest => ({
  fieldName: field.name,
  filename: file.name,
});

export type UploadLocationResource = Resource<UploadLocationRequest>;

export interface TypeAndFilename {
  type: string;
  filename: string;
}

export interface Base64Content {
  kind: 'base64-content';
  data: string;
}

export interface UploadedContent {
  kind: 'uploaded-content';
  uploadLocation: Link;
}

export const uploadedContent = (uploadLocation: Link): UploadedContent => ({
  kind: 'uploaded-content',
  uploadLocation,
});

export type NewFileContent = Base64Content | UploadedContent;

export interface NewFile extends TypeAndFilename {
  kind: 'new-file';
  file: Maybe<File>;
  dataUrl: Maybe<string>;
  content: Maybe<NewFileContent>;
}

export const newUploadedFile = (file: File, link: Maybe<Link>, dataUrl: string): NewFile => ({
  kind: 'new-file',
  content: link.map(uploadedContent),
  type: file.type,
  filename: file.name,
  file: just(file),
  dataUrl: just(dataUrl),
});

export interface NewFileOut extends TypeAndFilename {
  kind: 'new-file';
  content: Base64Content | UploadedContent;
}

export const newFileOut = (newFile: NewFile): Maybe<NewFileOut> =>
  newFile.content.map((content) => ({
    kind: 'new-file',
    content,
    type: newFile.type,
    filename: newFile.filename,
  }));

export interface KeepFile extends TypeAndFilename {
  kind: 'keep-file';
  data: Maybe<string>;
  src: URLParser;
}

export interface DeleteFile extends TypeAndFilename {
  kind: 'delete-file';
  data: Maybe<string>;
  src: URLParser;
}

export interface KeepFileOut extends TypeAndFilename {
  kind: 'keep-file';
  src: string;
}

export interface DeleteFileOut extends TypeAndFilename {
  kind: 'delete-file';
  src: string;
}

export type FileValue = NewFile | KeepFile | DeleteFile;

export type FileValueOut = NewFileOut | KeepFileOut | DeleteFileOut;

export const deleteKeptFile = (fileValue: FileValue): FileValue => {
  switch (fileValue.kind) {
    case 'new-file':
    case 'delete-file':
      return fileValue;
    case 'keep-file':
      return {
        ...fileValue,
        kind: 'delete-file',
      };
  }
};

export const isNotNew = (fileValue: FileValue): boolean => {
  switch (fileValue.kind) {
    case 'keep-file':
    case 'delete-file':
      return true;
    case 'new-file':
      return false;
  }
};

export const fileWithinSizeLimit = (field: FileField, file: File): Result<string, File> =>
  file.size < field.maxSize
    ? ok(file)
    : err(
        `File "${file.name}" of size ${formatBytesDecimal(
          file.size,
        )} is over limit of ${formatBytesDecimal(field.maxSize)}`,
      );

export const fileWithinSizeLimitT = (field: FileField, file: File): Task<string, File> =>
  fromResult(fileWithinSizeLimit(field, file));

export interface FileField extends FormFieldBase<'file'> {
  value: ReadonlyArray<FileValue>;
  accept: ReadonlyArray<string>;
  multiple: boolean;
  maxSize: number;
  createFileUploadLocationLink: Link;
}

export type FormField =
  | StringField
  | BooleanField
  | DateField
  | NumericField
  | SingleSelectionField
  | MultipleSelectionsField
  | FileField;

export interface FormTemplate {
  target: Rel;
  fields: ReadonlyArray<FormField>;
  errors: ReadonlyArray<string>;
}

export type FormTemplates = {
  [R in Rel]?: FormTemplate;
};

export interface HasFormTemplates {
  templates: FormTemplates;
}

export type FormOutValue = ReturnType<typeof fieldToValue>;

export type FormOutValues = {
  [K in string]: FormOutValue;
};

export const stringFieldOutValue = (field: StringField): string => field.value;

export const booleanFieldOutValue = (field: BooleanField): boolean => field.value;

export const dateFieldOutValue = (field: DateField): Maybe<string> =>
  field.value.map((date) => date.toISOString());

export const numberFieldOutValue = (field: NumericField): Maybe<number> => field.value;

export const singleSelectionsFieldOutValue = (field: SingleSelectionField): ReadonlyArray<string> =>
  fromNullable(field.value)
    .map((val) => [val.value])
    .getOrElse(() => []);

export const multipleSelectionsFieldOutValue = (
  field: MultipleSelectionsField,
): ReadonlyArray<string> => field.value.map((val) => val.value);

export const fileFieldOutValue = (field: FileField): ReadonlyArray<FileValueOut> =>
  mapMaybe((file): Maybe<FileValueOut> => {
    switch (file.kind) {
      case 'new-file':
        return newFileOut(file);
      case 'keep-file':
      case 'delete-file':
        return just({
          ...file,
          src: file.src.toString(),
          data: undefined,
        });
    }
  }, field.value);

export const fieldToValue = (field: FormField) => {
  switch (field.kind) {
    case 'string':
      return stringFieldOutValue(field);
    case 'boolean':
      return booleanFieldOutValue(field);
    case 'date':
      return dateFieldOutValue(field);
    case 'number':
      return numberFieldOutValue(field);
    case 'multiple-selection':
      return multipleSelectionsFieldOutValue(field);
    case 'single-selection':
      return singleSelectionsFieldOutValue(field);
    case 'file':
      return fileFieldOutValue(field);
  }
};

export const fieldToNameValue = (field: FormField): FormOutValues => ({
  [field.name]: fieldToValue(field),
});

export const isEditableField = (field: FormField): boolean => !isReadonly(field);

export const fieldsToValues = (
  fields: ReadonlyArray<FormField>,
): Result<ReadonlyArray<FormField>, FormOutValues> =>
  validateFields(fields.filter(isEditableField)).map((validFields) =>
    validFields.map(fieldToNameValue).reduce((values, value) => Object.assign(values, value)),
  );

export const fieldsToTemplate =
  (template: FormTemplate) =>
  (fields: ReadonlyArray<FormField>): FormTemplate => ({ ...template, fields });

export type FieldFor<K extends FormField['kind']> = Extract<FormField, { kind: K }>;

export function findField<K extends FormField['kind']>(
  name: string,
  kind: K,
): (fields: ReadonlyArray<FormField>) => Maybe<FieldFor<K>>;
export function findField<K extends FormField['kind']>(
  name: string,
  kind: K,
  fields: ReadonlyArray<FormField>,
): Maybe<FieldFor<K>>;
export function findField<K extends FormField['kind']>(
  name: string,
  kind: K,
  fields?: ReadonlyArray<FormField>,
) {
  const doit = (fields: ReadonlyArray<FormField>): Maybe<FieldFor<K>> => {
    return find(
      (f) => f.name === name,
      fields.filter((f): f is FieldFor<K> => f.kind === kind),
    );
  };
  return fields ? doit(fields) : doit;
}

export interface MissingFieldError {
  kind: 'missing-field-error';
  name: string;
  fieldKind: FormField['kind'];
}

export function findFieldT<K extends FormField['kind']>(
  name: string,
  kind: K,
): (fields: ReadonlyArray<FormField>) => Task<MissingFieldError, FieldFor<K>>;
export function findFieldT<K extends FormField['kind']>(
  name: string,
  kind: K,
  fields: ReadonlyArray<FormField>,
): Task<MissingFieldError, FieldFor<K>>;
export function findFieldT<K extends FormField['kind']>(
  name: string,
  kind: K,
  fields?: ReadonlyArray<FormField>,
) {
  const doit = (fields: ReadonlyArray<FormField>): Task<MissingFieldError, FieldFor<K>> => {
    return toTask(
      { kind: 'missing-field-error', name, fieldKind: kind },
      findField(name, kind, fields),
    );
  };
  return fields ? doit(fields) : doit;
}

export function fieldValue(
  name: string,
  kind: 'file',
  template: FormTemplate,
): Maybe<ReadonlyArray<FileValue>>;
export function fieldValue(
  name: string,
  kind: 'multiple-selection',
  template: FormTemplate,
): Maybe<ReadonlyArray<SelectionOption>>;
export function fieldValue(
  name: string,
  kind: 'single-selection',
  template: FormTemplate,
): Maybe<SelectionOption>;
export function fieldValue(name: string, kind: 'date', template: FormTemplate): Maybe<Date>;
export function fieldValue(name: string, kind: 'boolean', template: FormTemplate): Maybe<boolean>;
export function fieldValue(name: string, kind: 'number', template: FormTemplate): Maybe<number>;
export function fieldValue(name: string, kind: 'string', template: FormTemplate): Maybe<string>;
export function fieldValue<K extends FormField['kind']>(
  name: string,
  kind: K,
  template: FormTemplate,
) {
  switch (kind) {
    case 'date':
    case 'number':
      return findField<'date' | 'number'>(name, kind, template.fields).andThen<Date | number>(
        (f) => f.value,
      );
    case 'boolean':
    case 'file':
    case 'string':
    case 'multiple-selection':
    case 'single-selection':
      return findField(name, kind, template.fields).map((f) => f.value);
    default:
      assertNever(kind);
  }
}
